import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownRenderer from '../MarkdownRenderer';
import BlogAudioPlayer from '../BlogAudioPlayer';
import { BlogPost, getBlogPost, blogPosts } from '../../data/blogData';
import { setJsonLd } from '../../utils/seo';
import ScrollToTop from '../utils/ScrollToTop';

const BlogPostContent: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const [post, setPost] = useState<BlogPost | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (category && slug) {
      setIsLoading(true);
      // Small delay to show transition
      setTimeout(() => {
        const foundPost = getBlogPost(category, slug);
        setPost(foundPost);
        setIsLoading(false);
      }, 100);
    }
  }, [category, slug]);

  // Inject BlogPosting JSON-LD - moved before conditional returns to follow Rules of Hooks
  useEffect(() => {
    if (!post) return; // Guard clause instead of conditional hook calling
    
    const origin = window.location.origin;
    const url = `${origin}/blog/${post.categorySlug}/${post.slug}`;
    const image = post.image ? (post.image.startsWith('http') ? post.image : origin + post.image) : undefined;
    setJsonLd({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      ...(image ? { image: [image] } : {}),
      "datePublished": post.date,
      "author": { "@type": "Organization", "name": "Paokhum" },
      "publisher": { "@type": "Organization", "name": "Paokhum" },
      "url": url,
      "description": post.excerpt || undefined
    });
  }, [post]);

  if (!category || !slug) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/" replace />;
  }

  // Find current post index and get navigation posts
  const currentIndex = blogPosts.findIndex(p => p.id === post.id);
  const nextPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const prevPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
  // const isLatest = currentIndex === 0; // Reserved for future use

  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <motion.div
          key={post.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
        {/* Blog post header */}
        <section className="relative py-20 md:py-24 bg-secondary">
          <motion.div 
            className="container relative mx-auto px-3 sm:px-4 lg:px-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            {/* Breadcrumb */}
            <nav className="mb-8">
              <div className="flex items-center space-x-2 text-sm font-mono">
                <Link to="/" className="text-foreground/60 hover:text-primary transition-colors">
                  Home
                </Link>
                <span className="text-foreground/40">/</span>
                <Link to="/blog" className="text-foreground/60 hover:text-primary transition-colors">
                  Blog
                </Link>
                <span className="text-foreground/40">/</span>
                <span className="text-foreground/40">{post.category}</span>
              </div>
            </nav>
            
            {/* Category badge */}
            <motion.div 
              className="mb-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <span className="inline-block bg-primary text-primary-foreground font-mono text-xs uppercase tracking-widest px-3 py-1 rounded">
                {post.category}
              </span>
            </motion.div>
            
            {/* Title */}
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-display font-extrabold leading-tight mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              {post.title}
            </motion.h1>
            
            {/* Meta information */}
            <motion.div 
              className="flex items-center space-x-6 text-foreground/60 font-mono text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <span>{post.date}</span>
              <span>{post.readTime}</span>
            </motion.div>
          </motion.div>
        </section>
        
        {/* Blog post content */}
        <section className="relative py-16 md:py-20">
          <motion.div 
            className="container relative mx-auto px-3 sm:px-4 lg:px-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <article 
              className="max-w-4xl mx-auto"
              itemScope 
              itemType="http://schema.org/BlogPosting"
            >
              <meta itemProp="headline" content={post.title} />
              <meta itemProp="author" content="Paokhum" />
              <meta itemProp="datePublished" content={post.date} />
              {/* Audio Player - displays before content if audio is available */}
              {post.audio && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                >
                  <BlogAudioPlayer 
                    audioUrl={post.audio} 
                    title={post.title}
                  />
                </motion.div>
              )}
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="article-content"
                itemProp="articleBody"
              >
                <MarkdownRenderer content={post.content} className="prose prose-lg max-w-none" />
              </motion.div>
              
              {/* Blog navigation */}
              <motion.div 
                className="mt-16 pt-8 border-t border-foreground/20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.4 }}
              >
                {/* Previous/Next navigation */}
                <div className="flex flex-col sm:flex-row justify-between gap-6">
                  {prevPost && (
                    <Link 
                      to={`/blog/${prevPost.categorySlug}/${prevPost.slug}`}
                      className="group flex flex-col hover:transform hover:scale-105 transition-all duration-200"
                    >
                      <span className="text-sm text-foreground/60 font-mono uppercase tracking-wider mb-1">Previous</span>
                      <span className="text-foreground font-medium group-hover:underline decoration-primary transition-all">
                        {prevPost.title}
                      </span>
                    </Link>
                  )}
                  
                  {nextPost && (
                    <Link 
                      to={`/blog/${nextPost.categorySlug}/${nextPost.slug}`}
                      className="group flex flex-col hover:transform hover:scale-105 transition-all duration-200 sm:text-right"
                    >
                      <span className="text-sm text-foreground/60 font-mono uppercase tracking-wider mb-1">Next</span>
                      <span className="text-foreground font-medium group-hover:underline decoration-primary transition-all">
                        {nextPost.title}
                      </span>
                    </Link>
                  )}
                </div>
                
                {/* Call to action */}
                <div className="mt-12 p-8 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl border border-primary/30 backdrop-blur-sm">
                  <h3 className="text-2xl font-bold mb-3">Ready to elevate your business?</h3>
                  <p className="text-foreground/70 mb-6">
                    Discover how our services can help transform your digital presence.
                  </p>
                  <Link 
                    to="/#services" 
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-all duration-200 hover:shadow-lg hover:scale-105"
                  >
                    Learn more about our services
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </motion.div>
            </article>
          </motion.div>
        </section>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export default BlogPostContent;
